/**
 * vfx.js — celebration bursts.
 *
 * Two moments in the game are worth more than a toast: winning a prize
 * outright at a stall (GDD 7.4) and finishing a prize set. Both get a burst of
 * particles in front of the player, which is the cheapest way to make a reward
 * feel like one.
 *
 * Each burst is a single `THREE.Points` — one draw call, one geometry, one
 * material — animated on the CPU and disposed when it dies. At 40-odd
 * short-lived particles that is far cheaper than the alternative of a mesh per
 * fleck, and it keeps the renderer's draw-call count (visible in `?perf=1`)
 * flat no matter how lucky the player gets.
 *
 * **Its randomness is deliberately its own.** Drawing sparkle offsets from the
 * shared seeded generator would advance that stream by a variable amount
 * whenever a prize happened to drop, shifting every later gameplay random
 * number and desyncing stall tests elsewhere — the same hazard `space/fish-swim.js`
 * documents. A private mulberry32 keeps the bursts varied without touching it.
 *
 * @module ui/vfx
 */

import * as THREE from 'three';
import { EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { mulberry32 } from '../core/testmode.js';

/** Confetti palette — the midway's own colours. */
const CONFETTI = [0xffd166, 0xff7b54, 0x6fe7c4, 0xf25f7c, 0x9b5de5, 0xffffff];
const GRAVITY = -4.2;

/** Private generator: see the module comment. */
const vrng = mulberry32(0xc0ffee);

/** @returns {THREE.Texture} A soft round dot, built once. */
let dotTexture = null;
function dot() {
  if (dotTexture) return dotTexture;
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.45, 'rgba(255,255,255,0.85)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  dotTexture = new THREE.CanvasTexture(c);
  dotTexture.colorSpace = THREE.SRGBColorSpace;
  return dotTexture;
}

class VFX {
  constructor() {
    /** @type {THREE.Scene} */ this.scene = null;
    /** @type {THREE.Camera} */ this.camera = null;
    /** @type {Array<object>} */ this._bursts = [];
    this._installed = false;
  }

  /**
   * @param {{ scene: THREE.Scene, camera: THREE.Camera }} refs
   * @returns {VFX}
   */
  install({ scene, camera }) {
    if (this._installed) return this;
    this._installed = true;
    this.scene = scene;
    this.camera = camera;

    bus.on(EVENTS.DIRECT_PRIZE, () => this.confetti());

    // Set completion has no event of its own; it announces itself with this
    // cue, which is a stable enough hook to hang a sparkle on without reaching
    // into the meta layer.
    bus.on(EVENTS.AUDIO_PLAY, ({ name } = {}) => {
      if (name === 'set-complete') this.sparkle();
    });

    return this;
  }

  /**
   * A confetti burst in front of the player.
   * @param {{ count?: number, spread?: number, life?: number }} [opts]
   */
  confetti({ count = 44, spread = 3.4, life = 1.9 } = {}) {
    this._burst({
      count, life, size: 0.075,
      colors: CONFETTI,
      velocity: () => new THREE.Vector3(
        (vrng() - 0.5) * spread,
        1.9 + vrng() * 2.4,
        (vrng() - 0.5) * spread,
      ),
      gravity: GRAVITY,
    });
  }

  /** A tighter, all-gold shimmer for finishing a set. */
  sparkle({ count = 30, life = 1.4 } = {}) {
    this._burst({
      count, life, size: 0.055,
      colors: [0xffd166, 0xfff3c4, 0xffffff],
      velocity: () => {
        // A ring rather than a ball — it reads as a flourish, not an explosion.
        const a = vrng() * Math.PI * 2;
        const r = 1.5 + vrng() * 0.9;
        return new THREE.Vector3(Math.cos(a) * r, 0.6 + vrng() * 1.1, Math.sin(a) * r);
      },
      gravity: GRAVITY * 0.35,
    });
  }

  /** @private */
  _burst({ count, life, size, colors, velocity, gravity }) {
    if (!this.scene || !this.camera) return;

    const origin = this._front();
    const positions = new Float32Array(count * 3);
    const colorAttr = new Float32Array(count * 3);
    const vels = [];
    const c = new THREE.Color();

    for (let i = 0; i < count; i++) {
      positions[i * 3] = origin.x;
      positions[i * 3 + 1] = origin.y;
      positions[i * 3 + 2] = origin.z;
      c.setHex(colors[Math.floor(vrng() * colors.length)]);
      colorAttr[i * 3] = c.r;
      colorAttr[i * 3 + 1] = c.g;
      colorAttr[i * 3 + 2] = c.b;
      vels.push(velocity());
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colorAttr, 3));

    const mat = new THREE.PointsMaterial({
      size, map: dot(), vertexColors: true,
      transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, sizeAttenuation: true,
    });

    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    points.name = 'vfx-burst';
    this.scene.add(points);

    this._bursts.push({ points, geo, mat, vels, life, age: 0, gravity });
  }

  /**
   * Roughly where the player is looking, 1.7 m out and a little below eye
   * level, so a burst lands in the middle of the frame rather than over their
   * head in VR.
   * @returns {THREE.Vector3}
   * @private
   */
  _front() {
    const pos = new THREE.Vector3();
    const dir = new THREE.Vector3();
    this.camera.getWorldPosition(pos);
    this.camera.getWorldDirection(dir);
    return pos.add(dir.multiplyScalar(1.7)).add(new THREE.Vector3(0, -0.25, 0));
  }

  /** @param {number} dt */
  update(dt) {
    for (let i = this._bursts.length - 1; i >= 0; i--) {
      const b = this._bursts[i];
      b.age += dt;

      if (b.age >= b.life) {
        this.scene.remove(b.points);
        b.geo.dispose();
        b.mat.dispose();
        this._bursts.splice(i, 1);
        continue;
      }

      const arr = b.geo.attributes.position.array;
      for (let p = 0; p < b.vels.length; p++) {
        const v = b.vels[p];
        v.y += b.gravity * dt;
        arr[p * 3] += v.x * dt;
        arr[p * 3 + 1] += v.y * dt;
        arr[p * 3 + 2] += v.z * dt;
      }
      b.geo.attributes.position.needsUpdate = true;

      // Hold full brightness for the first third, then fade out.
      const t = b.age / b.life;
      b.mat.opacity = t < 0.33 ? 1 : 1 - (t - 0.33) / 0.67;
    }
  }

  /** Drop every live burst. */
  clear() {
    for (const b of this._bursts) {
      this.scene.remove(b.points);
      b.geo.dispose();
      b.mat.dispose();
    }
    this._bursts.length = 0;
  }

  /** @returns {number} Live bursts, for tests. */
  count() {
    return this._bursts.length;
  }
}

/** The one VFX pool. */
export const vfx = new VFX();
